/**
 * Shared paginator for the legal-policy views (Terms of Service,
 * Privacy Policy, EV Charging TOS, and the TNCDialogBox new-user
 * agree flow).
 *
 * Block model
 * -----------
 * Each top-level child of `.policy` is either a `.policy-section`
 * wrapper or a header element. For a section, we walk its direct
 * children and produce one block per child, with two exceptions:
 *
 *   - Bare `<ol>`, `<ul>`, and `<blockquote>` containers are
 *     transparent: the paginator pushes each of their children
 *     (li, p, etc.) as a standalone block. This lets long lists and
 *     multi-paragraph blockquotes break across pages.
 *
 *   - Anything wrapped in a `<div>` is treated as a single atomic
 *     block. This is the opt-in mechanism for keeping a group
 *     together (e.g., "You must not:" intro paired with its short
 *     list).
 *
 * Visibility model
 * ----------------
 * Pagination doesn't reflow the DOM; it hides off-page blocks via
 * `style.display = 'none'`. After the per-block pass, two cleanup
 * passes hide wrapper elements whose children are all hidden:
 *   - ol/ul/blockquote wrappers (whose default margins would otherwise
 *     leave a phantom gap on pages that contain none of their
 *     children),
 *   - `.policy-section` wrappers (whose padding/margin would do the
 *     same).
 *
 * Orphan-heading prevention
 * -------------------------
 * If a page ends with an h1/h2/h3, the heading is moved to the start
 * of the next page so it stays with its first paragraph.
 *
 * Numbering
 * ---------
 * Ordered lists must carry explicit `value="N"` attributes on each
 * `<li>` if numbering matters — browser CSS counters re-number visible
 * items when siblings are display:none, so the explicit value anchors
 * each item to its source-order position.
 */

export const NAV_HEIGHT = 56;
export const PAGE_TOP_PADDING = 20;

const TRANSPARENT_CONTAINERS = new Set(['OL', 'UL', 'BLOCKQUOTE']);

export function getBlockElements(policyRoot) {
  const blocks = [];
  for (const child of policyRoot.children) {
    if (child.classList.contains('policy-section')) {
      for (const sectionChild of child.children) {
        if (TRANSPARENT_CONTAINERS.has(sectionChild.tagName)) {
          for (const grandChild of sectionChild.children) blocks.push(grandChild);
        } else {
          blocks.push(sectionChild);
        }
      }
    } else {
      blocks.push(child);
    }
  }
  return blocks;
}

export function isHeading(block) {
  const tag = block.tagName;
  return tag === 'H1' || tag === 'H2' || tag === 'H3';
}

export function buildPages(blocks, availableHeight) {
  const newPages = [];
  let pageBlocks = [];
  let pageHeight = 0;

  for (let i = 0; i < blocks.length; i++) {
    const rect = blocks[i].getBoundingClientRect();
    const style = window.getComputedStyle(blocks[i]);
    const blockH = rect.height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);

    if (pageHeight + blockH > availableHeight && pageBlocks.length > 0) {
      newPages.push([...pageBlocks]);
      pageBlocks = [i];
      pageHeight = blockH;
    } else {
      pageBlocks.push(i);
      pageHeight += blockH;
    }
  }
  if (pageBlocks.length > 0) newPages.push(pageBlocks);

  for (let p = 0; p < newPages.length - 1; p++) {
    const page = newPages[p];
    if (page.length > 1) {
      const lastIdx = page[page.length - 1];
      if (isHeading(blocks[lastIdx])) {
        page.pop();
        newPages[p + 1].unshift(lastIdx);
      }
    }
  }

  return newPages;
}

export function applyPageVisibility(displayEl, pageBlockIndices) {
  const policyRoot = displayEl.querySelector('.policy');
  if (!policyRoot) return;

  const blocks = getBlockElements(policyRoot);
  const visibleSet = new Set(pageBlockIndices);

  blocks.forEach((block, i) => {
    block.style.display = visibleSet.has(i) ? '' : 'none';
  });

  for (const child of policyRoot.children) {
    if (child.classList.contains('policy-section')) {
      for (const sectionChild of child.children) {
        if (TRANSPARENT_CONTAINERS.has(sectionChild.tagName)) {
          const hasVisibleChild = Array.from(sectionChild.children).some(
            (c) => c.style.display !== 'none',
          );
          sectionChild.style.display = hasVisibleChild ? '' : 'none';
        }
      }
    }
  }

  for (const child of policyRoot.children) {
    if (child.classList.contains('policy-section')) {
      const hasVisible = Array.from(child.children).some((c) => c.style.display !== 'none');
      child.style.display = hasVisible ? '' : 'none';
    }
  }
}
